import {TextField} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {bgColorRequester, bgColorSpender} from '../../_helpers';

/**
 * Textfield to edit a number value, with a small display size.
 * A tooltip is displayed on top while hovering and while typing large numbers.
 *
 * @param {NumberTextfieldSmall.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function NumberTextfieldSmall({value, disabled, onChange, minValue = 0, decimalRegex, mandatoryForRequester, mandatoryForSpender, highlightMandatoryFieldForRequester, highlightMandatoryFieldForSpender, maxLength, sx = {}}) {
    const [open, setOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const [internalValue, setInternalValue] = useState(value);
    const [tooltipMessage, setTooltipMessage] = useState(value);

    // textfield value is stored internally
    // -> so that tooltip can show most recent value
    // -> onChange callback is called only when textfield loses focus
    useEffect(() => {
        setTooltipMessage(value);
        setInternalValue(value);
    }, [value]);

    const handleBlur = () => {
        setFocused(false);
        onChange(internalValue);
    };
    const handleFocus = () => {
        setFocused(true);
    };
    const handleOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };
    const handleChange = (event) => {
        let value = event.target.value;
        let maxLengthExceeded = false;
        if ( maxLength && value.length > maxLength) {
            value = value.substr(0, maxLength);
            maxLengthExceeded = true;
            setTooltipMessage(`Too many characters. Only ${maxLength} characters are allowed.`);
            setOpen(true);
        }
        // Here, we are using Regex to not let user enter decimals more than regex allows.
        if (decimalRegex) {
            value = handleDecimalsOnValue(value);
        }
        if (!maxLengthExceeded) {
            setTooltipMessage(value);
        }
        setInternalValue(value);
    };

    const handleDecimalsOnValue = (value) => {
        return value.match(decimalRegex)[0];
    };

    return <Tooltip
        title={tooltipMessage}
        open={(open || focused) && internalValue?.length > 2}
        onOpen={handleOpen}
        onClose={handleClose}
        placement={'top'}
        arrow >
        <TextField
            sx={{
                backgroundColor: mandatoryForSpender && highlightMandatoryFieldForSpender ?
                    bgColorSpender :
                    mandatoryForRequester && highlightMandatoryFieldForRequester ?
                        bgColorRequester :
                        null,
                ...sx,
            }}
            disabled={disabled}
            fullWidth
            value={internalValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            variant="outlined"
            size={'small'}
            type={'number'}
            inputProps={{min: minValue, step: 'any'}} // step: any removes the error message saying that we need to choose a value between x and y
        />
    </Tooltip>;
}

NumberTextfieldSmall.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    minValue: PropTypes.number,
    decimalRegex: PropTypes.shape(RegExp),
    mandatoryForSpender: PropTypes.bool,
    mandatoryForRequester: PropTypes.bool,
    highlightMandatoryFieldForRequester: PropTypes.bool,
    highlightMandatoryFieldForSpender: PropTypes.bool,
    maxLength: PropTypes.number,
    sx: PropTypes.object,
};
